import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { custApi, apiError } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center px-4">
      <div className="max-w-sm w-full border border-border p-8 bg-card">
        <div className="font-display font-extrabold tracking-tighter text-2xl mb-6">MorrCart</div>
        {children}
      </div>
    </div>
  );
}

export function MagicLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const ranRef = useRef(false);
  const custCtx = useCustomer();
  const store = params.get("store") || "";
  const [f, setF] = useState({ phone: "", email: "" });
  const [resend, setResend] = useState({ loading: false, sent: false, err: "" });

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); return; }
    custApi.post("/customer/magic-login/confirm", { token })
      .then(({ data }) => {
        localStorage.setItem("mc_customer_token", data.token);
        // Same CustomerProvider instance is reused on /portal (same tree position), so refresh it.
        if (custCtx?.refresh) custCtx.refresh();
        setState("ok");
        setTimeout(() => navigate("/portal" + (store ? `?store=${store}` : "")), 900);
      })
      .catch(() => setState("error"));
  }, [params, navigate, store]);

  const requestNew = async (e) => {
    e.preventDefault();
    setResend({ loading: true, sent: false, err: "" });
    try {
      await custApi.post(`/t/${store}/customer/magic-link`, { phone: f.phone, email: f.email });
      setResend({ loading: false, sent: true, err: "" });
    } catch (er) {
      setResend({ loading: false, sent: false, err: apiError(er.response?.data?.detail) || "Could not send link. Please try again." });
    }
  };

  return (
    <Shell>
      {state === "loading" && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="magic-loading"><Loader2 className="h-4 w-4 animate-spin" /> Signing you in…</div>}
      {state === "ok" && <div data-testid="magic-success"><CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" /><h2 className="font-display font-bold text-xl mt-3">Signed in</h2><p className="text-sm text-muted-foreground mt-1">Taking you to your account…</p></div>}
      {state === "error" && (
        <div data-testid="magic-error">
          <XCircle className="h-8 w-8 text-accent" />
          <h2 className="font-display font-bold text-xl mt-3">Link invalid or expired</h2>
          <p className="text-sm text-muted-foreground mt-1">Sign-in links expire for your security. Get a fresh one below.</p>
          {store && resend.sent ? (
            <p className="text-sm mt-4" data-testid="magic-resent-msg">If your phone and email match an account, we've emailed a new one-time sign-in link. Open it to sign in — no password needed.</p>
          ) : store ? (
            <form onSubmit={requestNew} className="mt-5 space-y-3" data-testid="magic-resend-form">
              <div><Label>Phone <span className="text-muted-foreground normal-case">(your account number)</span></Label><Input type="tel" className="rounded-none mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(415) 555-2671" required data-testid="magic-resend-phone" /></div>
              <div><Label>Email</Label><Input type="email" className="rounded-none mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="jane@email.com" required data-testid="magic-resend-email" /></div>
              {resend.err && <p className="text-sm text-accent" data-testid="magic-resend-error">{resend.err}</p>}
              <Button type="submit" disabled={resend.loading} className="w-full rounded-none" data-testid="magic-resend-submit">{resend.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a new sign-in link"}</Button>
            </form>
          ) : (
            <Button className="rounded-none mt-4" onClick={() => navigate("/portal")} data-testid="magic-goto-portal">Go to sign in</Button>
          )}
        </div>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); return; }
    custApi.post("/customer/verify-email/confirm", { token })
      .then(() => setState("ok")).catch(() => setState("error"));
  }, [params]);

  return (
    <Shell>
      {state === "loading" && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="verify-loading"><Loader2 className="h-4 w-4 animate-spin" /> Verifying your email…</div>}
      {state === "ok" && <div data-testid="verify-success"><CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" /><h2 className="font-display font-bold text-xl mt-3">Email verified</h2><p className="text-sm text-muted-foreground mt-1">Your email is confirmed. You can close this tab and return to your account.</p></div>}
      {state === "error" && <div data-testid="verify-error"><XCircle className="h-8 w-8 text-accent" /><h2 className="font-display font-bold text-xl mt-3">Link invalid or expired</h2><p className="text-sm text-muted-foreground mt-1">Request a new verification email from your account.</p></div>}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false); const [done, setDone] = useState(false);
  const token = params.get("token");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try { await custApi.post("/customer/password/reset", { token, password: pw }); setDone(true); }
    catch (er) { setErr(apiError(er.response?.data?.detail) || er.message); }
    finally { setLoading(false); }
  };

  if (!token) return <Shell><p className="text-sm text-accent" data-testid="reset-no-token">Missing reset token.</p></Shell>;
  return (
    <Shell>
      {done ? (
        <div data-testid="reset-success"><CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" /><h2 className="font-display font-bold text-xl mt-3">Password updated</h2><p className="text-sm text-muted-foreground mt-1">You can now sign in with your new password.</p><Button className="rounded-none mt-4" onClick={() => navigate("/portal")} data-testid="reset-goto-login">Go to sign in</Button></div>
      ) : (
        <form onSubmit={submit} data-testid="reset-form">
          <h2 className="font-display font-bold text-xl">Set a new password</h2>
          <div className="mt-4"><Label>New password</Label><Input type="password" className="rounded-none mt-1" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" /></div>
          <div className="mt-3"><Label>Confirm password</Label><Input type="password" className="rounded-none mt-1" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" /></div>
          {err && <p className="text-sm text-accent mt-2" data-testid="reset-error">{err}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-none mt-4" data-testid="reset-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
        </form>
      )}
    </Shell>
  );
}
